import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { isGuest, setGuest } from '../utils/guest';

const CATEGORIES = [
  'Explainers',
  'Events',
  'Concepts',
  'Key_Figures',
  'Applications',
  'Quotes',
  'Impact',
  'Controversies'
];

const AIDeckGenerator = () => {
  const { t } = useTranslation();

  const [topic, setTopic] = useState(() => localStorage.getItem('aiDeckTopic') || '');
  const [category, setCategory] = useState('Events');
  const [deck, setDeck] = useState(() => {
    const saved = localStorage.getItem('aiDeck');
    return saved ? JSON.parse(saved) : [];
  });
  const [loading, setLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [summary, setSummary] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(false);

  const generateDeck = async () => {
    if (!topic.trim()) return;
    setLoading(true);

    try {
      const res = await axios.post('https://amora-ai-service.onrender.com/generate-deck', {
        topic,
        category,  // 👈 this is required
        language: i18n.resolvedLanguage || i18n.language || 'en'
      });
      
      const generated = res.data.deck || [];

      const valid = generated.filter(e =>
        category === 'Events' ? /^\d{3,4}$/.test(String(e.year)) : true
      );
      
      const deduplicated = valid.filter(
        (e, i, self) => i === self.findIndex(ev => ev.title === e.title && ev.year === e.year)
      );
      
      setDeck(deduplicated);
      localStorage.setItem('aiDeck', JSON.stringify(deduplicated));
      localStorage.setItem('aiDeckTopic', topic);
      
    } catch (err) {
      console.error('Error generating deck:', err);
      alert(t('aiDeck.errorGenerate'));
    }

    setLoading(false);
  };

  const addToTimeline = async (event) => {
    try {
      await axios.post('https://amora-backend.onrender.com/api/student/custom-timeline', event);
      alert(t('messages.addToTimeline'));
    } catch (err) {
      console.error('Failed to add to timeline:', err);
      alert(t('messages.failAddEvent'));
    }
  };

  const fetchSummary = async (event) => {
    setSelectedCard(event);
    setSummary({});
    setLoadingSummary(true);
    try {
      const res = await axios.post('https://amora-ai-service.onrender.com/research-assistant', {
        title: event.title,
        description: event.description,
        language: i18n.resolvedLanguage || i18n.language || 'en'
      });
      setSummary(res.data);
    } catch (err) {
      setSummary({ ai_summary: t('aiDeck.errorFetchSummary') });
    }
    setLoadingSummary(false);
  };

  return (
    <div className="text-black relative">
      <h2 className="text-xl font-semibold uppercase border-b border-black mb-6 pb-2">{t('aiDeck.title')}</h2>

      <form
  onSubmit={(e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    if (topic !== localStorage.getItem('aiDeckTopic')) {
      localStorage.removeItem('aiDeck');
    }
    generateDeck();
  }}
  className="flex flex-col gap-2 mb-6 w-full"
>
  <div className="flex items-center gap-2 w-full flex-wrap sm:flex-nowrap">
    {/* Topic Input */}
    <div className="relative flex-grow min-w-[200px]">
      <input
        type="text"
        placeholder={t('aiDeck.placeholder')}
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        className="w-full pr-8 px-4 py-2 border border-black bg-white text-black font-bold tracking-wide uppercase placeholder:text-gray-400"
      />
      {topic && (
        <button
          type="button"
          onClick={() => setTopic('')}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-black text-lg font-bold focus:outline-none"
          aria-label="Clear input"
        >
          ✖
        </button>
      )}
    </div>

    {/* Category Dropdown */}
    <select
      value={category}
      onChange={(e) => setCategory(e.target.value)}
      className="min-w-[110px] h-[40px] border border-black bg-white text-black text-xs font-bold uppercase tracking-wide px-2"
    >
      {CATEGORIES.map((cat) => (
        <option key={cat} value={cat}>
          {{
            Explainers: 'Overview',
            Key_Figures: 'Figures',
          }[cat] || cat.replace('_', ' ')
        }
        </option>
      ))}
    </select>

    {/* Generate Button */}
    <button
      type="submit"
      className="h-[40px] min-w-[110px] border border-black bg-white text-black px-3 py-2 text-xs font-bold uppercase tracking-wide hover:bg-black hover:text-white transition"
    >
      {t('aiDeck.generate')}
    </button>
  </div>
</form>

<p className="text-xs text-gray-500 italic mt-1">
  {t('aiDeck.categoryHint', 'Enter your topic, choose a category, then click Generate.')}
</p>



      {loading && (
        <div className="text-sm text-black-400 mt-2 ml-1">
          ⏳ {t('aiDeck.loading') || 'Generating your deck...'}
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {deck.map((card, index) => (
          <div
            key={`${card.title}-${index}`}
            onClick={() => fetchSummary(card)}
            className="group border border-black bg-white text-black hover:bg-black hover:text-white p-4 transition font-bold tracking-wide uppercase"
          >
            <h3 className="text-xl font-bold group-hover:text-white mb-1">{card.title}</h3>
            <p className="text-sm group-hover:text-white mb-1">{card.year}</p>
            <p className="text-sm group-hover:text-white line-clamp-3">{card.description}</p>

            <div className="mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  addToTimeline(card);
                }}
                className="text-left w-full border border-black bg-white text-black text-xs px-3 py-2 uppercase font-bold tracking-wide hover:bg-black hover:text-white transition"
              >
                {t('aiDeck.addToTimeline')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedCard && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 px-4">
          <div className="bg-white text-black border border-black p-6 max-w-lg w-full relative shadow-none rounded-none max-h-screen overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-white"
              onClick={() => setSelectedCard(null)}
            >
              ✖
            </button>

            <h3 className="text-xl font-bold uppercase border-b border-black mb-2">{selectedCard.title}</h3>
            <p className="text-sm text-black mb-1">{selectedCard.year}</p>
            <p className="text-sm text-black mb-4">{selectedCard.description}</p>

            {loadingSummary ? (
              <p className="text-black-300">{t('aiDeck.loading')}</p>
            ) : (
              <>
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-black mb-1">Summary</h4>
                  <p className="text-sm text-black-300 whitespace-pre-wrap">{summary.ai_summary}</p>
                  {summary.did_you_know && (
                    <p className="mt-2 text-sm text-black-400 italic">{summary.did_you_know}</p>
                  )}
                </div>

                {summary.related_links?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-black mb-1">External Resources</h4>
                    {summary.related_links.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-black-400 hover:text-black-200 text-sm underline mb-1"
                      >
                        {link.title}
                      </a>
                    ))}
                  </div>
                )}
                {summary.recommended_searches?.length > 0 && (
                  <div className="mb-2">
                    <h4 className="text-lg font-semibold text-black mb-1">Try Searching:</h4>
                    <ul className="list-disc list-inside text-sm text-black-300">
                      {summary.recommended_searches.map((search, i) => (
                        <li key={i}>
                          <a
                            href={`https://www.google.com/search?q=${encodeURIComponent(search)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-gray-200"
                          >
                            {search}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {isGuest() && (
        <div className="fixed bottom-4 left-4 bg-black-800 text-black px-4 py-2 rounded shadow-lg z-50">
          🔒 {t('dashboard.guestPrompt') || 'Want to save your decks?'}{' '}
          <button onClick={() => window.location.href = '/login'} className="underline">
            {t('dashboard.signup') || 'Sign up here'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AIDeckGenerator;
